import { createTheme, ThemeOptions } from "@mui/material/styles";
import type { PaletteMode } from "@mui/material";

/**
 * Custom theme configuration for the Bucket List application
 * Supports both light and dark modes with custom color palettes
 */

const getThemeOptions = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    ...(mode === "light"
      ? {
          primary: {
            main: "#8CA9FF", // Soft blue/lavender
            light: "#bfd4ff",
            dark: "#5a7fcb",
            contrastText: "#ffffff",
          },
          secondary: {
            main: "#9c27b0", // Purple accent
            light: "#d05ce3",
            dark: "#6a0080",
            contrastText: "#ffffff",
          },
          background: {
            default: "#f5f5f5",
            paper: "#ffffff",
          },
          text: {
            primary: "#212121",
            secondary: "#757575",
          },
          success: {
            main: "#4caf50",
          },
          warning: {
            main: "#ff9800",
          },
          error: {
            main: "#f44336",
          },
        }
      : {
          primary: {
            main: "#bb86fc", // Vibrant purple
            light: "#f0b4ff",
            dark: "#885bc8",
            contrastText: "#000000",
          },
          secondary: {
            main: "#03dac6", // Teal accent
            light: "#66fff9",
            dark: "#00a896",
            contrastText: "#000000",
          },
          background: {
            default: "#000000", // Pure black
            paper: "#121212", // Slightly lighter for cards
          },
          text: {
            primary: "#ffffff",
            secondary: "#b0b0b0",
          },
          success: {
            main: "#03dac6",
          },
          warning: {
            main: "#ffb74d",
          },
          error: {
            main: "#cf6679",
          },
        }),
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2.5rem",
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: "1.75rem",
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.6,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.5,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.43,
    },
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "8px 16px",
          fontSize: "0.9375rem",
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow:
            mode === "light"
              ? "0px 2px 8px rgba(0, 0, 0, 0.1)"
              : "0px 2px 8px rgba(0, 0, 0, 0.3)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === "light" ? undefined : "#000000",
          boxShadow:
            mode === "light"
              ? "0px 1px 3px rgba(0, 0, 0, 0.12)"
              : "0px 1px 3px rgba(187, 134, 252, 0.3)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none", // Remove default gradient in dark mode
        },
      },
    },
  },
});

export const createAppTheme = (mode: PaletteMode) => {
  return createTheme(getThemeOptions(mode));
};
